/**
 * "Publish from your AI agent" modal, opened from the site-creation panel
 * (and from the Figma modal's convert-to-code path).
 *
 * The prompt is not shown until the user clicks "Generate prompt": that
 * click mints a fresh API token through the exact Account API-tokens write
 * (shared generateApiToken helper, label "agent-publish", only the sha256
 * hash stored) and splices the raw token into the prompt. Raw tokens are
 * unrecoverable, so a fresh one is generated on every click even when the
 * account already has tokens.
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import firebase from '../../firebase';
import { generateApiToken } from '../../utils/apiTokens';

// Aero hosting is reached by calling its REST API directly with curl and the
// freshly minted bearer token — no install, no MCP registration — so the
// copyable prompt works in any agent with a shell and network.
function defaultBuildPrompt(token, siteId, siteName) {
  // Target a specific pre-assigned site id (the slug) so updates land on exactly
  // this site; fall back to name targeting only when no id is available.
  const targetParam = siteId ? `siteId=${siteId}` : `siteName=${siteName}`;
  return (
    `Build this project if needed, then zip the build output with index.html at the zip root and deploy it to Aero by calling its HTTP API directly. Do not install anything.\n\n` +
    `Upload inline: POST the zip to https://aeroapp.ai/api/deploy-site?${targetParam} with headers Authorization: Bearer ${token} and Content-Type: application/zip. ` +
    `If the response says the upload is too large (sites over ~4MB, e.g. image-heavy folders), use the signed-URL flow instead: POST the file manifest to https://aeroapp.ai/api/deploy-upload-urls, ` +
    `PUT each file to the signed URLs it returns, then POST https://aeroapp.ai/api/deploy-finalize. Each JSON response describes the exact next step.\n\n` +
    `Reply with the live URL.`
  );
}

const DEFAULT_STEPS = [
  'Copy the prompt. It includes your personal publish token.',
  'Paste it into your AI tool.',
  'Your agent replies with the live link. Every publish appears in Versions.',
];

const DEFAULT_LIMITATIONS = [
  'Works with any agent that has a shell and network: Cowork, Claude Code, Cursor, Codex. No install required.',
  'Publish built static files only, with index.html at the root.',
  'Large sites use the signed-URL upload steps included in the prompt.',
];

/**
 * Shared MCP "do it from your AI agent" modal. Defaults publish a site; the
 * New Test flow passes a test-oriented heading/steps/prompt while reusing the
 * same token mint. `name` lets callers without a site doc supply the label.
 */
function AgentPublishModal({
  user,
  site,
  name,
  siteId: siteIdProp,
  heading = 'Publish from your AI agent',
  steps = DEFAULT_STEPS,
  limitations = DEFAULT_LIMITATIONS,
  buildPrompt = defaultBuildPrompt,
  onClose,
}) {
  const [prompt, setPrompt] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const siteName = name || site?.title || site?.generatedUrl || 'my project';
  // The doc id IS the slug; targeting it makes the agent's deploy precise.
  const siteId = siteIdProp || site?.id || null;

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    setError(null);
    try {
      const { token, hashedToken, prefix } = await generateApiToken();
      await firebase.firestore()
        .collection('user').doc(user.uid)
        .collection('apiKeys')
        .add({
          hashedToken,
          label: 'agent-publish',
          prefix,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          lastUsedAt: null,
        });
      // The raw token lives only in this prompt until the modal closes.
      setPrompt(buildPrompt(token, siteId, siteName));
    } catch (err) {
      console.error('Error generating publish token:', err);
      setError('Could not generate the prompt. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return ReactDOM.createPortal(
    <div className="ut-modal-root aero-modal-backdrop ut-theme" onClick={onClose}>
      <div className="aero-modal aero-sites-agent-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aero-modal-head">
          <h2>{heading}</h2>
          <button
            type="button"
            className="aero-btn aero-btn-ghost aero-btn-icon"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="aero-modal-body">
          <ol className="aero-sites-step-list">
            {steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>

          {prompt ? (
            <div className="aero-sites-prompt-box">
              <pre>{prompt}</pre>
              <button
                type="button"
                className="aero-btn aero-btn-secondary aero-btn-sm aero-sites-prompt-copy"
                onClick={handleCopy}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          ) : (
            <div className="aero-sites-prompt-generate">
              <button
                type="button"
                className="aero-btn aero-btn-primary"
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? 'Generating…' : 'Generate prompt'}
              </button>
            </div>
          )}
          <p className="aero-sites-field-note">
            One token works for all your sites. Revoke anytime in Account, API tokens.
          </p>
          {error && <p className="aero-sites-field-error">{error}</p>}

          <ul className="aero-sites-limitation-list">
            {limitations.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AgentPublishModal;
